import {
  Checkbox,
  SvgIcon,
  type SxProps,
  type Theme,
  useTheme,
} from "@mui/material";
import { SelectedCheckCircleIcon } from "components/icons";
import { memo } from "react";

interface CircleCheckboxProps {
  checked: boolean;
  disabled?: boolean;
  onChange?: () => void;
  size?: "sm" | "md" | "lg";
  /**
   * Colour of the checked disc. Defaults to brand purple 700, the app's
   * selected-state colour (purple 25 row, purple 700 check). Pass a colour to
   * override it; the disabled state is unaffected either way.
   */
  checkedColor?: string;
  "aria-label"?: string;
  sx?: SxProps<Theme>;
}

const sizes = {
  sm: "20px",
  md: "24px",
  lg: "28px",
};

const UncheckedCircleIcon = () => (
  <SvgIcon>
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  </SvgIcon>
);

export const CircleCheckbox = memo(
  ({
    checked,
    disabled,
    onChange,
    size = "md",
    checkedColor,
    sx,
    ...rest
  }: CircleCheckboxProps) => {
    const theme = useTheme();
    const dimension = sizes[size];

    const resolvedCheckedColor = disabled
      ? theme.palette.neutral["020"]
      : (checkedColor ?? theme.palette.primitive.brand.purple[700]);

    return (
      <Checkbox
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
        disableRipple
        icon={<UncheckedCircleIcon />}
        checkedIcon={<SelectedCheckCircleIcon />}
        sx={{
          width: dimension,
          height: dimension,
          padding: 0,
          color: theme.palette.neutral["020"],
          "&.Mui-checked": {
            color: resolvedCheckedColor,
          },
          "&.Mui-disabled": {
            color: theme.palette.neutral["020"],
          },
          ...sx,
        }}
        {...rest}
      />
    );
  },
);

CircleCheckbox.displayName = "CircleCheckbox";
